import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Wrench, ScanLine, Minimize2, Ruler, FileText, Droplets, X, ArrowRight, Sparkles,
} from "lucide-react";

const QUICK_TOOLS = [
  { to: "/tools/passport", label: "Passport Photo", icon: ScanLine, color: "from-blue-500/25 to-cyan-400/15" },
  { to: "/tools/compress", label: "Compress Image", icon: Minimize2, color: "from-emerald-500/25 to-teal-400/15" },
  { to: "/tools/resize", label: "Resize Image", icon: Ruler, color: "from-purple-500/25 to-pink-400/15" },
  { to: "/tools/img-to-pdf", label: "Images → PDF", icon: FileText, color: "from-amber-500/25 to-orange-400/15" },
  { to: "/tools/watermark", label: "Add Watermark", icon: Droplets, color: "from-rose-500/25 to-red-400/15" },
];

const HIDDEN_PATHS = ["/tools", "/login", "/register"];

export default function FloatingToolsWidget() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const panelRef = useRef(null);

  // Hide on /tools/* routes (already on tools) and auth pages
  const hidden = HIDDEN_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // Close on route change
  useEffect(() => { setOpen(false); }, [pathname]);

  if (hidden) return null;

  return (
    <div
      ref={panelRef}
      className="fixed left-5 bottom-5 z-40 lg:left-6 lg:bottom-6"
      data-testid="floating-tools-widget"
    >
      {/* Panel */}
      <div
        className={`absolute bottom-16 left-0 w-[300px] origin-bottom-left transition-all duration-200 ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
        }`}
        data-testid="floating-tools-panel"
        aria-hidden={!open}
      >
        <div className="rounded-2xl border border-white/10 bg-[#0F172A]/95 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 border border-amber-500/40 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-amber-300">
                <Sparkles className="h-3 w-3" /> FREE
              </span>
              <span className="text-sm font-bold text-white">Free Utility Tools</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="grid h-6 w-6 place-items-center rounded-md text-white/55 hover:bg-white/5 hover:text-white"
              aria-label="Close tools panel"
              data-testid="floating-tools-close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="p-2">
            {QUICK_TOOLS.map((t) => {
              const Icon = t.icon;
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-white/[0.05] transition-colors group"
                  data-testid={`floating-tool-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '')}`}
                >
                  <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-gradient-to-br ${t.color} border border-white/10`}>
                    <Icon className="h-4 w-4 text-white/90" />
                  </div>
                  <span className="text-sm font-medium text-white/85 group-hover:text-white flex-1">{t.label}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-white/30 group-hover:text-cyan-300 group-hover:translate-x-0.5 transition-all" />
                </Link>
              );
            })}
          </div>

          <Link
            to="/tools"
            className="block text-center text-xs font-semibold text-cyan-300 hover:text-white border-t border-white/[0.06] px-4 py-3 hover:bg-white/[0.03] transition-colors"
            data-testid="floating-tools-view-all"
          >
            View all 12 tools →
          </Link>
        </div>
      </div>

      {/* Trigger Button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative grid h-12 w-12 place-items-center rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 shadow-[0_8px_24px_-6px_rgba(34,211,238,0.65)] hover:from-blue-500 hover:to-cyan-400 active:scale-95 transition-all"
        aria-label="Open free tools menu"
        aria-expanded={open}
        data-testid="floating-tools-trigger"
      >
        <Wrench className="h-5 w-5 text-white" />
        {!open && (
          <span className="absolute -top-1 -right-1 grid h-4 w-4 place-items-center rounded-full bg-amber-400 text-[9px] font-extrabold text-amber-950 ring-2 ring-[#0A101D]">
            12
          </span>
        )}
      </button>
    </div>
  );
}
